import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Upload, Download, FileSpreadsheet, X } from 'lucide-react';

export default function EmployeesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [importing, setImporting] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [departments, setDepartments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() =>
    location.state?.openEmployeeId || location.state?.openEmployee ? 1000 : 20
  );
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    full_name: '',
    department: '',
    designation: '',
    email: '',
    date_of_joining: '',
    status: 'Active',
    date_of_exit: '',
  });

  const [searchTerm, setSearchTerm] = useState('');

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/employees/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const fetchEmployees = useCallback(async () => {
    try {
      const params = { page, pageSize };
      if (selectedDepartment && selectedDepartment !== 'all') {
        params.department = selectedDepartment;
      }
      if (selectedStatus && selectedStatus !== 'all') {
        params.status = selectedStatus;
      }
      if (searchTerm && searchTerm.trim() !== '') {
        params.search = searchTerm.trim();
      }
      
      const response = await api.get('/employees', { params });
      const res = response.data;
      
      if (res && typeof res === 'object' && 'data' in res) {
        setEmployees(res.data || []);
        setTotalCount(res.total ?? 0);
      } else if (Array.isArray(res)) {
        setEmployees(res);
        const totalFromHeader = response.headers['x-total-count'];
        setTotalCount(totalFromHeader ? parseInt(totalFromHeader, 10) : res.length);
      } else {
        setEmployees(res?.employees || []);
        setTotalCount(res?.total ?? 0);
      }
    } catch (error) {
      toast.error('Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  }, [selectedDepartment, selectedStatus, pageSize, page, searchTerm]);

  useEffect(() => {
    fetchDepartments();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [selectedDepartment, selectedStatus, pageSize, searchTerm]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Employees list is already filtered server-side by search/department/status.
  const filteredEmployees = employees;

  const handleOpenDialog = (employee = null) => {
    if (employee) {
      setEditingEmployee(employee);
      const dateVal = employee.date_of_joining;
      const dateStr = dateVal ? String(dateVal).slice(0, 10) : '';
      const exitDateVal = employee.date_of_exit;
      const exitDateStr = exitDateVal ? String(exitDateVal).slice(0, 10) : '';
      setFormData({
        full_name: employee.full_name,
        department: employee.department,
        designation: employee.designation,
        email: employee.email,
        date_of_joining: dateStr,
        status: employee.status,
        date_of_exit: exitDateStr,
      });
    } else {
      setEditingEmployee(null);
      setFormData({
        full_name: '',
        department: '',
        designation: '',
        email: '',
        date_of_joining: '',
        status: 'Active',
        date_of_exit: '',
      });
    }
    setDialogOpen(true);
  };

  // Open employee details when navigated from global search
  useEffect(() => {
    const openEmployee = location.state?.openEmployee;
    const openEmployeeId = location.state?.openEmployeeId;

    // If full employee object is passed from global search, use it directly
    if (openEmployee) {
      handleOpenDialog(openEmployee);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    if (!openEmployeeId) return;

    const openFromListOrSearch = async () => {
      // Try to find employee in the already-fetched list first (any page / filters)
      if (employees.length > 0) {
        const employeeFromList = employees.find(
          (e) => String(e.employee_id) === String(openEmployeeId)
        );

        if (employeeFromList) {
          handleOpenDialog(employeeFromList);
          navigate(location.pathname, { replace: true, state: {} });
          return;
        }
      }

      // Fallback: query employees endpoint with search on employee_id
      try {
        const response = await api.get('/employees', {
          params: {
            search: openEmployeeId,
            page: 1,
            pageSize: 20,
          },
        });

        const res = response.data;
        const list = Array.isArray(res) ? res : res?.data || [];
        const fromSearch = list.find(
          (e) => String(e.employee_id) === String(openEmployeeId)
        );

        if (fromSearch) {
          handleOpenDialog(fromSearch);
        }
      } catch (error) {
        console.error('Failed to open employee from global search:', error);
      } finally {
        navigate(location.pathname, { replace: true, state: {} });
      }
    };

    openFromListOrSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openEmployeeId/openEmployee from global search, open dialog once
  }, [employees, location.state?.openEmployee, location.state?.openEmployeeId, location.pathname, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        await api.put(`/employees/${editingEmployee.employee_id}`, formData);
        toast.success('Employee updated successfully');
      } else {
        await api.post('/employees', formData);
        toast.success('Employee added successfully');
      }
      setDialogOpen(false);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (employeeId) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;

    try {
      await api.delete(`/employees/${employeeId}`);
      toast.success('Employee deleted successfully');
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to delete employee');
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/employees/export', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employees.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Employees exported successfully');
    } catch (error) {
      toast.error('Failed to export employees');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/employees/template', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employees_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/employees/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`Some rows had errors: ${response.data.errors.length} errors`);
      }
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import employees');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B1F3A]">Employees</h1>
          <p className="text-slate-600 mt-2 text-sm sm:text-base">Manage employee records</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleImport}
            className="hidden"
            data-testid="import-file-input"
          />
          <Button
            data-testid="download-template-button"
            variant="outline"
            onClick={handleDownloadTemplate}
            className="border-slate-300 text-xs sm:text-sm"
            size="sm"
          >
            <FileSpreadsheet className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">Template</span>
          </Button>
          <Button
            data-testid="import-employees-button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="border-slate-300 text-xs sm:text-sm"
            size="sm"
          >
            <Upload className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">{importing ? 'Importing...' : 'Import'}</span>
            <span className="sm:hidden">{importing ? '...' : 'Imp'}</span>
          </Button>
          <Button
            data-testid="export-employees-button"
            variant="outline"
            onClick={handleExport}
            className="border-slate-300 text-xs sm:text-sm"
            size="sm"
          >
            <Download className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">Export</span>
          </Button>
          <Button data-testid="add-employee-button" onClick={() => handleOpenDialog()} className="bg-[#0B1F3A] hover:bg-[#0B1F3A]/90 text-xs sm:text-sm" size="sm">
            <Plus className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">Add Employee</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
      </div>

      {/* Compact Filters Section (like Assets/Assignments) */}
      <div className="bg-white rounded-lg border border-border p-3 mb-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-2 w-full sm:min-w-[220px] sm:flex-1">
            <Input
              id="search-employees"
              placeholder="Search by name, email, department, designation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-9 text-sm"
            />
          </div>
          <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
            <SelectTrigger id="department-filter" className="h-9 w-full sm:w-[180px] text-sm">
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departments.map((dept) => (
                <SelectItem key={dept} value={dept}>
                  {dept}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger id="status-filter" className="h-9 w-full sm:w-[160px] text-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Exit">Exit</SelectItem>
            </SelectContent>
          </Select>
          {(selectedDepartment !== 'all' || selectedStatus !== 'all' || searchTerm) && (
            <>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground ml-auto">
                <span>{filteredEmployees.length} of {employees.length}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedDepartment('all');
                  setSelectedStatus('all');
                  setSearchTerm('');
                }}
                className="h-9 px-3"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">ID</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">Name</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">Department</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">Designation</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">Email</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">Status</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredEmployees.map((employee) => (
                  <tr key={employee.employee_id} className="hover:bg-muted/50 transition-colors" data-testid={`employee-row-${employee.employee_id}`}>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm whitespace-nowrap">{employee.employee_id}</td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium break-words min-w-[120px]">{employee.full_name}</td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm break-words">{employee.department}</td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm break-words">{employee.designation}</td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm break-words">{employee.email}</td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm whitespace-nowrap">
                      <span
                        className={`px-2 py-1 rounded-md text-xs font-medium ${
                          employee.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {employee.status}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm whitespace-nowrap">
                      <div className="flex gap-1 sm:gap-2">
                        <Button
                          data-testid={`edit-employee-${employee.employee_id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(employee)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                        <Button
                          data-testid={`delete-employee-${employee.employee_id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(employee.employee_id)}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-muted/30 border-t border-border">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            {totalCount > 20 ? (
              <>
                <p className="text-xs sm:text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
                  Show{' '}
                  <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                    <SelectTrigger className="h-8 w-[70px] text-xs sm:text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="40">40</SelectItem>
                      <SelectItem value="60">60</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  {' '}| Total Count: <span className="font-semibold text-[#0B1F3A]">{totalCount}</span>
                  {selectedDepartment !== 'all' && (
                    <span className="ml-2">in <span className="font-medium">{selectedDepartment}</span> department</span>
                  )}
                  {selectedStatus !== 'all' && (
                    <span className="ml-2">status: <span className="font-medium">{selectedStatus}</span></span>
                  )}
                </p>
                <div className="flex items-center gap-1 text-xs sm:text-sm">
                  <Button variant="outline" size="sm" className="h-8 px-2" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                    &laquo; Previous
                  </Button>
                  <span className="px-2 font-medium text-[#0B1F3A]">{page}/{Math.max(1, Math.ceil(totalCount / pageSize))}</span>
                  <Button variant="outline" size="sm" className="h-8 px-2" disabled={page >= Math.max(1, Math.ceil(totalCount / pageSize))} onClick={() => setPage((p) => Math.min(Math.ceil(totalCount / pageSize), p + 1))}>
                    Next &raquo;
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-xs sm:text-sm font-semibold text-[#0B1F3A]">
                Total: {totalCount} employee{totalCount !== 1 ? 's' : ''}
                {selectedDepartment !== 'all' && (
                  <span className="ml-2 text-muted-foreground font-normal">in {selectedDepartment} department</span>
                )}
                {selectedStatus !== 'all' && (
                  <span className="ml-2 text-muted-foreground font-normal">status: {selectedStatus}</span>
                )}
              </p>
            )}
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">{editingEmployee ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  data-testid="employee-name-input"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="employee-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Input
                  id="department"
                  data-testid="employee-department-input"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="designation">Designation</Label>
                <Input
                  id="designation"
                  data-testid="employee-designation-input"
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date_of_joining">Date of Joining</Label>
                <Input
                  id="date_of_joining"
                  data-testid="employee-doj-input"
                  type="date"
                  value={formData.date_of_joining}
                  onChange={(e) => setFormData({ ...formData, date_of_joining: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      status: value,
                      // Clear exit date when switching back to Active to avoid accidental reuse
                      date_of_exit: value === 'Active' ? '' : prev.date_of_exit,
                    }));
                  }}
                >
                  <SelectTrigger data-testid="employee-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Exit">Exit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.status === 'Exit' && (
                <div className="space-y-2">
                  <Label htmlFor="date_of_exit">Date of Exit</Label>
                  <Input
                    id="date_of_exit"
                    data-testid="employee-doe-input"
                    type="date"
                    value={formData.date_of_exit}
                    onChange={(e) => setFormData({ ...formData, date_of_exit: e.target.value })}
                    required={formData.status === 'Exit'}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button data-testid="employee-form-submit" type="submit">
                {editingEmployee ? 'Update' : 'Add'} Employee
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}